import React, { useState, useEffect } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import DOMPurify from 'dompurify';
import { Award, ArrowRight, FileText, CalendarDays } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { api, fileUrl } from '../lib/api';
import { SectionHeader, Rich, Loading } from '../components/Shared';
import { useSectionOverride } from '../lib/pageContent';
import { FlyerDownloadSection } from '../components/FlyerDownload';
import { CvDownloadSection } from '../components/CvDownload';
import { toYouTubeEmbed } from '../lib/youtube';

// Parse "Label | URL" lines from an admin-editable textarea into link objects
const parseSectionLinks = (text) => (text || '')
  .split('\n').map((l) => l.trim()).filter(Boolean)
  .map((l) => {
    const idx = l.indexOf('|');
    if (idx === -1) return null;
    const label = l.slice(0, idx).trim();
    const url = l.slice(idx + 1).trim();
    return label && url ? { label, url } : null;
  })
  .filter(Boolean);

const isInternal = (url) => url.startsWith('/') && !url.startsWith('/api/');

// Small link chip used under experience categories and story blocks.
// newTab forces opening in a new window/tab (with safe rel handling).
const ToolChip = ({ link, newTab = false }) => {
  const cls = 'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[#1a2744]/25 bg-white text-sm font-bold text-[#1a2744] hover:border-[#C9A84C] hover:bg-[#FBF7EC] transition-colors duration-150';
  if (isInternal(link.url)) {
    return <Link to={link.url} target={newTab ? '_blank' : undefined} rel={newTab ? 'noopener noreferrer' : undefined} className={cls} data-testid="experience-tool-chip">{link.label} <ArrowRight className="h-3.5 w-3.5 text-[#C9A84C]" /></Link>;
  }
  const external = link.url.startsWith('http');
  return (
    <a href={link.url} target={external || newTab ? '_blank' : undefined} rel={external || newTab ? 'noopener noreferrer' : undefined} className={cls} data-testid="experience-tool-chip">
      {link.label} <ArrowRight className="h-3.5 w-3.5 text-[#C9A84C]" />
    </a>
  );
};

// Callout buttons (gold primary + outline white)
const ToolButton = ({ link, primary = false }) => {
  const cls = primary
    ? 'h-12 px-5 bg-[#C9A84C] hover:bg-[#b8973f] text-[#1a2744] font-bold btn-press w-full sm:w-auto justify-center'
    : 'h-12 px-5 border-white/40 bg-transparent text-white hover:bg-white/10 hover:text-white font-bold btn-press w-full sm:w-auto justify-center';
  const inner = isInternal(link.url)
    ? <Link to={link.url}>{link.label}</Link>
    : <a href={link.url} target={link.url.startsWith('http') ? '_blank' : undefined} rel={link.url.startsWith('http') ? 'noopener noreferrer' : undefined}>{link.label}</a>;
  return <Button asChild variant={primary ? 'default' : 'outline'} className={cls} data-testid="experience-tool-button">{inner}</Button>;
};

// Public attached media (only items Steve explicitly made public)
const AttachedMedia = ({ items }) => {
  if (!items || items.length === 0) return null;
  return (
    <div className="mt-4 space-y-3" data-testid="story-attached-media">
      {items.map((m) => m.kind === 'image' ? (
        <figure key={m.id} className="rounded-xl border border-slate-200 bg-slate-50 p-3" data-testid="attached-media-image">
          <img src={fileUrl(m.file_url)} alt={m.alt_text || m.title} className="rounded-lg w-full max-h-96 object-contain bg-white" />
          {(m.caption || m.source_note) && (
            <figcaption className="text-xs text-slate-500 italic mt-1.5">
              {m.caption}{m.caption && m.source_note ? ' · ' : ''}{m.source_note}
            </figcaption>
          )}
        </figure>
      ) : (
        <a key={m.id} href={fileUrl(m.file_url)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 mr-4 text-sm font-bold text-[#1a2744] underline decoration-[#C9A84C] decoration-2 underline-offset-4" data-testid="attached-media-document">
          <FileText className="h-4 w-4" /> {m.title || 'Supporting document'}
        </a>
      ))}
    </div>
  );
};

const StoryImage = ({ s, className = '' }) => (
  <figure className={className} data-testid="story-image-figure">
    <img src={fileUrl(s.image_url)} alt={s.image_alt || s.title} className="rounded-xl w-full max-h-80 object-cover" />
    {s.image_caption && <figcaption className="text-xs text-slate-500 italic mt-1.5">{s.image_caption}</figcaption>}
  </figure>
);

export const StoryTimeline = ({ stories, mediaByStory = {} }) => (
  <div className="relative">
    <div className="absolute left-4 top-2 bottom-2 w-0.5 bg-slate-200 hidden sm:block" />
    <div className="space-y-6">
      {stories.map((s) => {
        const showImage = !!s.image_url && s.image_show !== false;
        const pos = s.image_position || 'top';
        const sideImage = showImage && (pos === 'left' || pos === 'right');
        return (
        <div key={s.id} className="relative sm:pl-12" data-testid="story-block">
          <span className="hidden sm:block absolute left-2.5 top-6 h-3.5 w-3.5 rounded-full bg-[#C9A84C] ring-4 ring-[#F3F5F8]" />
          <Card className="card-lift">
            <CardContent className="p-5 sm:p-6">
              {s.timeline_date && <p className="text-xs font-bold text-[#C9A84C] uppercase tracking-wider mb-1 flex items-center gap-1"><CalendarDays className="h-3.5 w-3.5" /> {s.timeline_date}</p>}
              <h3 className="font-serif font-bold text-lg text-[#1a2744] leading-snug">{s.title}</h3>
              {showImage && pos === 'top' && <StoryImage s={s} className="mt-3" />}
              {sideImage ? (
                <div className={`mt-3 flex flex-col sm:flex-row gap-4 ${pos === 'right' ? 'sm:flex-row-reverse' : ''}`}>
                  <StoryImage s={s} className="sm:w-2/5 shrink-0" />
                  <div className="flex-1"><Rich html={s.body_html} /></div>
                </div>
              ) : (
                <div className="mt-3"><Rich html={s.body_html} /></div>
              )}
              {showImage && pos === 'below' && <StoryImage s={s} className="mt-3" />}
              {s.clipping_url && (
                <figure className="mt-4 rounded-xl border border-slate-200 bg-slate-50 p-3" data-testid="story-clipping">
                  <img src={fileUrl(s.clipping_url)} alt={s.clipping_caption || `${s.title} clipping`} className="rounded-lg w-full max-h-96 object-contain bg-white" />
                  {s.clipping_caption && <figcaption className="text-xs text-slate-500 italic mt-1.5">{s.clipping_caption}</figcaption>}
                </figure>
              )}
              {(() => {
                const storyLinks = parseSectionLinks(s.links_text);
                if (!storyLinks.length) return null;
                return (
                  <div className="mt-4 pt-3 border-t border-slate-100" data-testid="story-tool-links">
                    <div className="flex flex-wrap gap-2">
                      {storyLinks.map((l, i) => <ToolChip key={i} link={l} newTab />)}
                    </div>
                  </div>
                );
              })()}
              <AttachedMedia items={mediaByStory[s.id]} />
              {s.video_embed && <div className="aspect-video mt-3"><iframe src={toYouTubeEmbed(s.video_embed) || s.video_embed} title={s.title} className="w-full h-full rounded-xl" loading="lazy" allowFullScreen allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" /></div>}
              {s.doc_url && (
                <a href={fileUrl(s.doc_url)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 mt-3 mr-4 text-sm font-bold text-[#1a2744] underline decoration-[#C9A84C] decoration-2 underline-offset-4">
                  <FileText className="h-4 w-4" /> View supporting document
                </a>
              )}
              {s.link_url && (
                <a href={s.link_url} target={s.link_url.startsWith('/') ? undefined : '_blank'} rel="noopener noreferrer" className="inline-flex items-center gap-1.5 mt-3 text-sm font-bold text-[#1a2744] underline decoration-[#C9A84C] decoration-2 underline-offset-4" data-testid="story-related-link">
                  <ArrowRight className="h-4 w-4" /> {s.link_label || 'Related link'}
                </a>
              )}
              {s.source_notes && <p className="text-xs text-slate-400 mt-3 italic">{s.source_notes}</p>}
            </CardContent>
          </Card>
        </div>
        );
      })}
    </div>
  </div>
);

// Approved "why running" reason cards (shared with /start-here)
export const WHY_REASONS = [
  { title: 'Bring real change', body: 'Not more of the same. Practical change residents can actually see and measure.' },
  { title: 'Update systems that have fallen behind', body: 'City systems and processes need to catch up so residents get answers without the runaround.' },
  { title: 'Help residents who feel unheard', body: 'Too many people feel passed around or left without answers. Steve is running for them.' },
  { title: 'Listen, document, follow up, report back', body: 'Every concern deserves a record, a follow-up, and a real answer, not a dead end.' },
  { title: 'Protect taxpayers', body: 'Respect for every tax dollar, with spending residents can see and question.' },
  { title: 'Fairness, transparency, accountability', body: 'Standing up for residents with open information and honest answers.' },
  { title: 'Build practical tools', body: 'Like PITA, the Ward 8 Public Issue Tracker App: report it, map it, track it, get answers.' },
];

// Experience sections/projects/skills/awards renderer (shared with /start-here)
export const ExperienceContent = ({ expPage, publicMedia = [] }) => {
  const expSections = (expPage?.sections || []).filter((s) => s.published !== false);
  const toolsCallout = expSections.find((s) => s.style === 'callout');
  const normalSections = expSections.filter((s) => s.style !== 'callout');
  return (
    <div className="mt-8">
      {normalSections.map((section) => {
        const links = parseSectionLinks(section.links_text);
        return (
        <div key={section.id} className="mb-8">
          <SectionHeader kicker={section.kicker} title={section.title} />
          <Card><CardContent className="p-5 sm:p-8">
            <Rich html={section.body_html} />
            <AttachedMedia items={publicMedia.filter((m) => m.attached_section_id === section.id)} />
            {links.length > 0 && (
              <div className="mt-5 pt-4 border-t border-slate-100" data-testid="experience-section-links">
                <p className="text-[11px] font-black uppercase tracking-widest text-[#C9A84C] mb-2">See how this experience connects</p>
                <div className="flex flex-wrap gap-2">
                  {links.map((l, i) => <ToolChip key={i} link={l} />)}
                </div>
              </div>
            )}
          </CardContent></Card>
        </div>
        );
      })}
      {(() => {
        const projects = (expPage?.projects || []).filter((p) => p.published !== false);
        if (!projects.length) return null;
        return (
          <div className="mb-10">
            <SectionHeader kicker="SELECTED PROJECTS" title={expPage?.projects_title || 'Selected Web and Business Development Projects'} />
            {expPage?.projects_intro && <p className="text-slate-600 leading-relaxed max-w-3xl mb-5" data-testid="experience-projects-intro">{expPage.projects_intro}</p>}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="experience-projects-grid">
              {projects.map((p) => (
                <Card key={p.id} className="card-lift" data-testid="experience-project-card">
                  <CardContent className="p-5">
                    <h3 className="font-serif font-bold text-[#1a2744] leading-snug">{p.title}</h3>
                    <p className="text-sm text-slate-700 leading-relaxed mt-2">{p.body}</p>
                    {p.link_url && (
                      <a href={p.link_url} target={p.link_url.startsWith('http') ? '_blank' : undefined} rel="noopener noreferrer" className="inline-flex items-center gap-1.5 mt-3 text-sm font-bold text-[#1a2744] underline decoration-[#C9A84C] decoration-2 underline-offset-4" data-testid="experience-project-link">
                        <ArrowRight className="h-3.5 w-3.5" /> Related link
                      </a>
                    )}
                    {p.source_note && <p className="text-xs text-slate-400 italic mt-3" data-testid="experience-project-source">Source: {p.source_note}</p>}
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        );
      })()}
      <div className="mb-10"><CvDownloadSection loc="experience" /></div>
      <SectionHeader sectionKey="experience.skills" kicker="SKILLS" title="Areas of experience" />
      <div className="flex flex-wrap gap-2 mb-10">
        {(expPage?.skills || []).map((s, i) => (
          <span key={i} className="px-3 py-1.5 bg-white border border-slate-200 rounded-full text-sm font-semibold text-[#1a2744]" data-testid="experience-skill">{s}</span>
        ))}
      </div>
      <SectionHeader sectionKey="experience.highlights" kicker="HIGHLIGHTS" title="Experience highlights" />
      <Card className="mb-10"><CardContent className="p-5 sm:p-8">
        <ul className="space-y-2.5">
          {(expPage?.highlights || []).map((h, i) => (
            <li key={i} className="flex gap-2.5 text-slate-700" data-testid="experience-highlight"><span className="h-2 w-2 rounded-full bg-[#C9A84C] mt-2.5 shrink-0" /> {h}</li>
          ))}
        </ul>
      </CardContent></Card>
      <SectionHeader sectionKey="experience.recognition" kicker="RECOGNITION" title="Awards" />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {(expPage?.awards || []).map((a) => (
          <Card key={a.id} className="card-lift" data-testid="experience-award">
            <CardContent className="p-5 flex gap-3 items-start">
              <Award className="h-6 w-6 text-[#C9A84C] shrink-0 mt-0.5" />
              <p className="font-semibold text-[#1a2744] text-sm leading-relaxed">{a.title}</p>
            </CardContent>
          </Card>
        ))}
      </div>
      {toolsCallout && (
        <div className="mt-10 bg-[#1a2744] rounded-2xl p-6 sm:p-10" data-testid="experience-tools-callout">
          {toolsCallout.kicker && <p className="text-xs font-black uppercase tracking-widest text-[#C9A84C]">{toolsCallout.kicker}</p>}
          <h2 className="font-serif font-black text-white text-2xl sm:text-3xl mt-2">{toolsCallout.title}</h2>
          <div className="mt-3 text-slate-300 leading-relaxed max-w-3xl [&_p]:mb-3 [&_p:last-child]:mb-0" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(toolsCallout.body_html || '') }} />
          {toolsCallout.callout_text && <p className="mt-4 border-l-4 border-[#C9A84C] pl-3.5 font-serif font-bold text-[#e8c96a] text-lg" data-testid="experience-tools-callout-line">{toolsCallout.callout_text}</p>}
          {parseSectionLinks(toolsCallout.links_text).length > 0 && (
            <div className="mt-6 flex flex-col sm:flex-row flex-wrap gap-3" data-testid="experience-tools-callout-buttons">
              {parseSectionLinks(toolsCallout.links_text).map((l, i) => (
                <ToolButton key={i} link={l} primary={i === 0} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default function MeetSteve() {
  const location = useLocation();
  const initialTab = location.pathname.endsWith('/story') ? 'story'
    : location.pathname.endsWith('/experience') ? 'experience'
    : location.pathname.endsWith('/bio') ? 'bio' : 'why';
  const [tab, setTab] = useState(initialTab);
  const [page, setPage] = useState(null);
  const [expPage, setExpPage] = useState(null);
  const [stories, setStories] = useState([]);
  const [publicMedia, setPublicMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  // Admin -> Page Content override for the Meet Steve banner (null = built-in wording)
  const heroOverride = useSectionOverride('meet-steve.header');

  useEffect(() => {
    Promise.allSettled([api.get('/pages/meet-steve'), api.get('/pages/experience'), api.get('/stories'), api.get('/story-media/public')]).then(([p, e, s, m]) => {
      setPage(p.value?.data || null);
      setExpPage(e.value?.data || null);
      setStories(s.value?.data || []);
      setPublicMedia(Array.isArray(m.value?.data) ? m.value.data : []);
      setLoading(false);
    });
  }, []);

  useEffect(() => { setTab(initialTab); }, [initialTab]);

  if (loading) return <Loading />;

  // Full-width top banner (Steve full-body on white, no cropping).
  // Admin can override via the meet-steve page hero_image; falls back to the provided banner.
  const bannerImage = page?.hero_image || '/api/uploads/steve-fullbody-banner.webp';

  // Banner wording: Page Content override -> Pages settings -> built-in default
  const heroKicker = heroOverride ? heroOverride.kicker : 'MEET STEVE';
  const heroTitle = (heroOverride && heroOverride.title) || page?.title || 'Meet Steve Lesperance';
  const heroIntro = heroOverride ? (heroOverride.intro || page?.subtitle) : page?.subtitle;

  // The "Why is Steve Running" CMS section (if present) opens the Why tab;
  // remaining sections belong to the Bio tab.
  const publishedSections = (page?.sections || []).filter((s) => s.published !== false);
  const whySection = publishedSections.find((s) => /why/i.test(s.title || ''));
  const bioSections = publishedSections.filter((s) => s !== whySection);

  return (
    <div>
      {/* Hero — full-width top banner, white background, image never cropped */}
      <div className="bg-white border-b border-slate-200" data-testid="meet-steve-hero">
        {/* Mobile: text above the banner so it stays readable */}
        <div className="md:hidden px-4 pt-8 pb-2 max-w-6xl mx-auto">
          {heroKicker && <p className="text-xs font-semibold uppercase tracking-widest text-[#8a6f22] mb-2">{heroKicker}</p>}
          <h1 className="text-3xl font-black text-[#1a2744] tracking-tight leading-tight">{heroTitle}</h1>
          {heroIntro && <p className="mt-3 text-base text-slate-600 leading-relaxed">{heroIntro}</p>}
          <p className="mt-3 text-sm font-bold text-[#8a6f22]">Lesperance is the French word for Hope</p>
        </div>
        <div className="relative" data-testid="meet-steve-hero-image">
          <img src={fileUrl(bannerImage)} alt="Steve Lesperance, Ward 8 Councillor candidate, full portrait" className="w-full h-auto block" />
          {/* Desktop: text overlaid on the banner's white left side */}
          <div className="hidden md:flex absolute inset-y-0 left-0 w-[52%] items-center">
            <div className="pl-8 lg:pl-16 xl:pl-24 pr-6 max-w-2xl">
              {heroKicker && <p className="text-xs lg:text-sm font-semibold uppercase tracking-widest text-[#8a6f22] mb-3">{heroKicker}</p>}
              <h1 className="text-3xl lg:text-5xl xl:text-6xl font-black text-[#1a2744] tracking-tight leading-tight">{heroTitle}</h1>
              {heroIntro && <p className="mt-4 text-base lg:text-lg text-slate-600 leading-relaxed">{heroIntro}</p>}
              <p className="mt-4 text-sm lg:text-base font-bold text-[#8a6f22]">Lesperance is the French word for Hope</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="bg-white border border-slate-200 h-auto p-1 w-full lg:w-auto grid grid-cols-2 sm:grid-cols-4 lg:inline-flex gap-1" data-testid="meet-steve-tabs">
            <TabsTrigger value="why" className="h-10 px-3 sm:px-4 font-bold text-xs sm:text-sm data-[state=active]:bg-[#1a2744] data-[state=active]:text-white" data-testid="tab-why">Why is Steve Running</TabsTrigger>
            <TabsTrigger value="bio" className="h-10 px-3 sm:px-4 font-bold text-xs sm:text-sm data-[state=active]:bg-[#1a2744] data-[state=active]:text-white" data-testid="tab-bio">Steve's Bio</TabsTrigger>
            <TabsTrigger value="story" className="h-10 px-3 sm:px-4 font-bold text-xs sm:text-sm data-[state=active]:bg-[#1a2744] data-[state=active]:text-white" data-testid="tab-story">Steve's Story</TabsTrigger>
            <TabsTrigger value="experience" className="h-10 px-3 sm:px-4 font-bold text-xs sm:text-sm data-[state=active]:bg-[#1a2744] data-[state=active]:text-white" data-testid="tab-experience">Experience</TabsTrigger>
          </TabsList>

          {tab === 'why' && (
            <div className="mt-8" data-testid="why-running-tab">
              <SectionHeader sectionKey="meet-steve.why" kicker="WHY IS STEVE RUNNING" title="Real change, working systems, and answers for Ward 8" />
              {whySection && (
                <Card className="mb-8"><CardContent className="p-5 sm:p-8"><Rich html={whySection.body_html} /></CardContent></Card>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="why-reason-cards">
                {WHY_REASONS.map((r) => (
                  <Card key={r.title} className="card-lift" data-testid="why-reason-card">
                    <CardContent className="p-5">
                      <p className="font-serif font-bold text-[#1a2744]">{r.title}</p>
                      <p className="text-sm text-slate-600 mt-1.5 leading-relaxed">{r.body}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
              <div className="mt-10 bg-[#1a2744] rounded-2xl p-6 sm:p-10 text-center" data-testid="why-slogan-band">
                <p className="font-serif font-black text-white text-xl sm:text-2xl leading-snug">"I'm helping the people by building the tools needed to get the job done."</p>
                <p className="mt-3 text-slate-300">Steve promises accountability by building the system for it.</p>
                <p className="mt-4 text-sm font-black uppercase tracking-widest text-[#C9A84C]">Truth Before Spin. Answers Before Excuses. Service Before Politics.</p>
                <div className="mt-6 flex flex-col sm:flex-row justify-center gap-3">
                  <Button asChild className="h-12 bg-[#C9A84C] hover:bg-[#b8973f] text-[#1a2744] font-bold btn-press"><Link to="/issue-tracker">Ward 8 Issue Tracker App (PITA) <ArrowRight className="h-4 w-4 ml-1" /></Link></Button>
                  <Button asChild variant="outline" className="h-12 border-white/40 bg-transparent text-white hover:bg-white/10 hover:text-white font-bold btn-press"><Link to="/platform">See Steve's Platform</Link></Button>
                </div>
              </div>
            </div>
          )}

          {tab === 'bio' && (
            <div className="mt-8">
              {bioSections.map((section) => (
                <div key={section.id} className="mb-10">
                  <SectionHeader kicker={section.kicker} title={section.title} />
                  <Card><CardContent className="p-5 sm:p-8"><Rich html={section.body_html} /></CardContent></Card>
                </div>
              ))}
              <SectionHeader kicker="WHY VOTE STEVE?" title="Eight reasons Ward 8 residents are backing Steve" />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {(page?.why_vote_cards || []).map((card) => (
                  <Card key={card.id} className="card-lift" data-testid="why-vote-card">
                    <CardContent className="p-5">
                      <p className="font-serif font-bold text-[#1a2744]">{card.title}</p>
                      <p className="text-sm text-slate-600 mt-1.5 leading-relaxed">{card.body}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
              <div className="mt-10 bg-[#1a2744] rounded-2xl p-6 sm:p-8 text-center">
                <p className="font-serif font-black text-white text-xl sm:text-2xl">Steve promises accountability by building the system for it.</p>
                <Button asChild className="mt-4 h-12 bg-[#C9A84C] hover:bg-[#b8973f] text-[#1a2744] font-bold btn-press"><Link to="/issue-tracker">Visit the Ward 8 Issue Tracker App (PITA) <ArrowRight className="h-4 w-4 ml-1" /></Link></Button>
              </div>
              <div className="mt-6 space-y-4">
                <CvDownloadSection loc="meet-steve" />
                <FlyerDownloadSection loc="meet-steve" />
              </div>
            </div>
          )}

          {tab === 'story' && (
            <div className="mt-8">
              <SectionHeader kicker="STEVE'S STORY" title="A life of communication, systems, and standing up" dek="Concerns described below are raised in the public interest. Supporting documents and source material are uploaded where available." />
              {stories.length === 0 ? <p className="text-slate-500">Story blocks are being prepared.</p> : <StoryTimeline stories={stories} mediaByStory={publicMedia.reduce((acc, m) => { if (m.attached_story_id) { (acc[m.attached_story_id] = acc[m.attached_story_id] || []).push(m); } return acc; }, {})} />}
            </div>
          )}

          {tab === 'experience' && <ExperienceContent expPage={expPage} publicMedia={publicMedia} />}
        </Tabs>
      </div>
    </div>
  );
}
